import { useCallback, useRef } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { MODAL_Z_INDEX } from "../../constants"
import { AppStatus, PRODUCT_DESTINATION } from "../../constants/enums"
import {
  productOverlaySelector,
  productPageOverlaySelector
} from "../../recoil/atoms/product-overlay"
import { roomSelector } from "../../recoil/atoms/room"
import { exitFullScreen, openLinkOnNewTab } from "../../util"
import { useBrowserType } from "../useBrowserType"
import useMeetingClient from "../useMeetingClient"
import useWindowSize from "../useWindowSize"
import { isMobileDevice } from "../../util/browser"

export const useCloseProductPageOverlay = () => {
  const meetingClient = useMeetingClient()
  const setProductOverlay = useSetRecoilState(productOverlaySelector)
  const setProductPageOnOverlay = useSetRecoilState(productPageOverlaySelector)

  /** This function will close the product overlay and return main window video to origin poistion from floating window  */
  const closeProductPageOverlay = useCallback(e => {
    e?.preventDefault()
    e?.stopPropagation()

    if (window.onbeforeunload) window.onbeforeunload = null

    // Handle native miniplayer exit, if native app support present return
    const isNativeAppSupportPresent = meetingClient?.nativeBridge?.eventMiniPlayerExpandButtonPressed()
    if(isNativeAppSupportPresent) return true

    // Handle parent iframe page close
    window.MeetOnline?.client()?.parentPageCommunication?.closeOverlayCheckoutPage()

    const mainVideoDiv = document.getElementById(
      "main-mvw-container"
    ) as HTMLDivElement

    if (mainVideoDiv) {
      mainVideoDiv.style.zIndex = null
      mainVideoDiv.style.position = null
      mainVideoDiv.style.right = null
      mainVideoDiv.style.bottom = null
      mainVideoDiv.style.width = null
      mainVideoDiv.style.borderRadius = null
      mainVideoDiv.style.overflow = null
    }
    setProductPageOnOverlay({ url: "" })
    setProductOverlay({ isOpen: false })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return closeProductPageOverlay
}

function useProductPageOverlay() {
  const meetingClient = useMeetingClient()
  const { isAndroidMobile } = useBrowserType()
  const { screenSize } = useWindowSize()
  const isMobile = isMobileDevice(screenSize)
  const isMobileRef = useRef(isMobile)
  isMobileRef.current = isMobile // to use in callback function with no dependence

  const room = useRecoilValue(roomSelector)
  const { productPageOpening } = room ?? {}
  const setProductPageOnOverlay = useSetRecoilState(productPageOverlaySelector)

  const closeProductPageOverlay = useCloseProductPageOverlay()

  /** This function will make Main Window Video full screen */
  const makeMainWindowVideoFullScreen = useCallback(() => {
    // Make MWV full screen
    const mainVideoDiv = document.getElementById(
      "main-mvw-container"
    ) as HTMLDivElement

    mainVideoDiv.style.zIndex = MODAL_Z_INDEX * 10 + ""
    mainVideoDiv.style.position = "fixed"
    mainVideoDiv.style.left = "0px"
    mainVideoDiv.style.bottom = "0px"
    mainVideoDiv.style.width = "100vw"
    mainVideoDiv.style.height = "100vh"
    mainVideoDiv.style.minHeight = "100%"
    mainVideoDiv.style.borderRadius = "0px"

    exitFullScreen()
  }, [])

  /** This method opens product page on parent iframe */
  const openProductPageOnParentIFrame = useCallback(url => {
    try {
      const isSuccess =
        meetingClient.parentPageCommunication?.openOverlayCheckoutPage(url, {
          addDraggableOverlay: isAndroidMobile,
          isSmallDevice: isMobileRef.current
        })

      // Check if success
      if (!isSuccess) return false

      setProductPageOnOverlay({
        url,
        location: PRODUCT_DESTINATION.PARENT_IFRAME
      })

      makeMainWindowVideoFullScreen()

      // close the floating window, if user click back button
      window.onbeforeunload = e => {
        closeProductPageOverlay(e)
      }

      return true
    } catch (error) {
      if (error.status === AppStatus.IN_PROGRESS) {
        return true
      }

      return false
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openProductPageOverlay = useCallback(
    url => {
      exitFullScreen()

      // Make MWV full screen
      const mainVideoDiv = document.getElementById(
        "main-mvw-container"
      ) as HTMLDivElement

      if (!mainVideoDiv.style.zIndex) mainVideoDiv.style.display = "none"
      // mainVideoDiv.style.width = FLOATING_VIDEO_SIZE.widthPx
      // mainVideoDiv.style.height = FLOATING_VIDEO_SIZE.heightPx

      setProductPageOnOverlay({
        url,
        location: PRODUCT_DESTINATION.LIVESELL_IFRAME,
        isOpen: true
      })
    },
    [setProductPageOnOverlay]
  )

  /**
   *  This method decide where to open the product page and handle the main window video accordingly
   */
  const openFullScreenProductPage = useCallback(
    async ({ url, showVariantScreen, openOverlayProduct, fallbackHandler }) => {
      exitFullScreen()
      
      // Check if site is open on native app and does native app support buy handle
      const isNativeAppSupportPresent = meetingClient?.nativeBridge?.eventBuyButtonPressed({ productUrl : url })
      if (isNativeAppSupportPresent) return true


      // EXTERNAL_WITH_NO_MINIPLAYER means open the product papge on external without pip or iframe
      if(productPageOpening === PRODUCT_DESTINATION.EXTERNAL_WITH_NO_MINIPLAYER) {
        openLinkOnNewTab(url)
        return 
      }

      if (
        showVariantScreen &&
        (productPageOpening === PRODUCT_DESTINATION.LIVESELL_IFRAME ||
          document.referrer) &&
        openOverlayProduct
      ) {
        // show variant selector with floating video
        openProductPageOverlay(null) // set url null, avoid url to open on iframe
        openOverlayProduct()
      } else if (showVariantScreen) {
        // handle fallback
        fallbackHandler?.()
      } else {
        // 3rd case - Open product on parent iframe, if failed try to open it on set way on dashboard (PIP or In App iframe)
        const isSuccessfullyOpened = openProductPageOnParentIFrame(url)

        if (!isSuccessfullyOpened) {
          if (productPageOpening === PRODUCT_DESTINATION.LIVESELL_IFRAME) {
            openProductPageOverlay(url)
          } else {
            // handle fallback
            fallbackHandler?.()
          }
        }
      }
    },
    [
      productPageOpening,
      openProductPageOnParentIFrame,
      openProductPageOverlay,
      makeMainWindowVideoFullScreen,
      setProductPageOnOverlay
    ]
  )

  return {
    closeProductPageOverlay,
    openFullScreenProductPage
  }
}

export default useProductPageOverlay
